import _ from 'lodash';
import { isTargetDateBeforeComparisonDateByNDays } from '../../../utils/StringDateUtil';
import { NegotiationInfoOnCustomerInfoList } from '../../../external/CustomerListApi';
import { splitStringByLength } from '../../../utils/StringUtil';

const COUNT_OVER_TEXT = '・・・';

/**
 * お客さま情報カードの左側（お客さま氏名・お客さま番号・担当者・店舗名）の表示テキストを
 * 改行考慮して取得します。
 *
 * - 入力文字列が12文字未満の場合はそのまま返却します（改行処理不要のため）。
 * - それ以外は全角12文字相当（内部ではslice長さ24）で分割し、改行コード '\n' で結合して返します。
 *
 * @param input - 表示対象の文字列
 * @returns 改行を含む表示用文字列（改行不要なら元の文字列）
 */
export const getLeftAreaDisplayText = (input: string, customLength?: number) => {
  const length = customLength || 12;
  const halfWidthLength = length * 2; // 全角文字は2文字分としてカウントするため、slice長さは倍にする
  // 12文字未満の場合は判定処理自体が不要となるため、文字列の加工をスキップする
  if (input.length < length) {
    return input;
  }
  // メモ 商談一覧は全角8文字で折り返していたが、こちらの画面は幅に余裕があるため全角12文字で折り返す仕様に変更
  // 24文字で改行するために文字列分割関数を実行
  const arrayString = splitStringByLength(input, halfWidthLength);
  // 改行コードで配列をjoinします
  return arrayString.join('\n');
};

/**
 *  更新日が今日から60日以上前かどうかを判定します。
 *
 * - updateDate または today が falsy な値（例: null、空文字など）の場合は false を返します。
 * - updateDate と today はともに有効な日付文字列であることを前提としています。無効な日付文字列が渡された場合の挙動は保証されません。
 * - updateDate が today より60日以上前の場合は true を返し、それ以外の場合は false を返します。
 * @param updateDate
 * @param today
 * @returns 更新日が今日から60日以上前の場合は true、それ以外の場合は false
 */
export const isUpdateDateOver60DaysFromToday = (updateDate: string, today: string): boolean => {
  return isTargetDateBeforeComparisonDateByNDays(updateDate, today, 60);
};
const blankToHyphen = (value: string): string => {
  return value === '' ? '-' : value;
};

const stringZeroToHyphen = (value: string): string => {
  return value === '0' ? '-' : value;
};

/**
 * 商談一覧のテンポラリコードを「商談名 : tempCd1,tempCd2,tempCd3,tempCd4」の形式で取得します。
 *
 * - negotiationList が空の場合は ['なし'] を返します。
 * - negotiationList の要素が2件以下の場合は全要素を変換して返します。
 * - negotiationList の要素が3件以上の場合は先頭2件を変換して末尾に件数超過を示す定数（'・・・'）を付与して返します。
 *
 * @param negotiationList - CustomerInfoList 表示用の商談情報配列
 * @returns 変換後の表示文字列配列（要素が存在しない場合は ['なし']、多い場合は末尾に '・・・' を付与）
 */
export const getDisplayTemporaryCodesWithNegotiationName = (negotiationList: NegotiationInfoOnCustomerInfoList[]): string[] => {
  if (negotiationList.length === 0) {
    return ['なし'];
  }

  if (negotiationList.length <= 2) {
    return negotiationList.map(
      (code) =>
        `${code.negotiationName} : ${stringZeroToHyphen(code.tempCd1)},${blankToHyphen(code.tempCd2)},${blankToHyphen(
          code.tempCd3,
        )},${blankToHyphen(code.tempCd4)}`,
    );
  }

  return _.take(negotiationList, 2)
    .map(
      (code) =>
        `${code.negotiationName} : ${stringZeroToHyphen(code.tempCd1)},${blankToHyphen(code.tempCd2)},${blankToHyphen(
          code.tempCd3,
        )},${blankToHyphen(code.tempCd4)}`,
    )
    .concat([COUNT_OVER_TEXT]);
};
